import { useState, useRef, useEffect, useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import Header from '../component/Header';
import Footer from '../component/footer';
import { ChevronLeft, ChevronRight, Download, PlayCircle, Menu, X } from 'lucide-react';
import { CourseRowData, courseRows } from '../utils/librarydata';
import { getLibraryRows } from '../services/libraryService';
import { getUserDashboard } from '../services/userService';
import { getAuthToken } from '../utils/authSession';
import LearnerLiveClassesPanel from '../components/live/LearnerLiveClassesPanel';

// =========================================
// INDIVIDUAL SLIDER COMPONENT
// =========================================
const CourseSlider = ({
  row,
  sectionId,
  userPlanId,
  onRestrictedVideoClick,
}: {
  row: CourseRowData;
  sectionId: string;
  userPlanId: number | null;
  onRestrictedVideoClick: () => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position to enable/disable buttons
  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft) < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scrollRow = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const firstCard = container.querySelector('[data-course-card]'); 
      const gap = 24; 
      
      const scrollAmount = firstCard ? firstCard.clientWidth + gap : container.clientWidth; 
      container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const canOpenCard = (planIds?: number[]): boolean => {
    if (!planIds?.length) {
      return true;
    }

    if (userPlanId === null || userPlanId === undefined) {
      return true;
    }

    return planIds.includes(userPlanId);
  };

  return (
    <section id={sectionId} className="relative scroll-mt-24 lg:scroll-mt-12">
      
      {/* Section Header */}
      <div className="flex items-center justify-between gap-4 mb-4 md:mb-6">
        <h2 className="text-lg md:text-2xl font-bold text-[#113A28] tracking-tight">
          {row.title}
        </h2>
        
        {/* Scroll Controls */}
        <div className="flex items-center flex-shrink-0 gap-2">
          <button 
            onClick={() => scrollRow('left')}
            disabled={!canScrollLeft}
            className={`w-9 h-9 md:w-10 md:h-10 rounded-full border border-gray-200 flex items-center justify-center transition-all
              ${!canScrollLeft 
                ? 'bg-gray-50 cursor-not-allowed opacity-80' 
                : 'bg-white text-[#113A28] hover:border-[#4CE470] hover:text-[#4CE470] hover:shadow-md cursor-pointer'
              }
            `}
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
          </button>
          <button 
            onClick={() => scrollRow('right')}
            disabled={!canScrollRight}
            className={`w-9 h-9 md:w-10 md:h-10 rounded-full border border-gray-200 flex items-center justify-center transition-all
              ${!canScrollRight 
                ? 'bg-gray-50 cursor-not-allowed opacity-80' 
                : 'bg-white text-[#113A28] hover:border-[#4CE470] hover:text-[#4CE470] hover:shadow-md cursor-pointer'
              }
            `}
          >
            <ChevronRight size={20} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Horizontal Scrolling Card Container */}
      <div 
        ref={scrollRef}
        onScroll={checkScroll}
        className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-8 pt-2 -mt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden scroll-smooth"
      >
        {row.cards.map((card, cardIndex) => {
          
          // Outer dark green wrapper
          const cardClassName = "bg-[rgb(17,58,40)] p-3 sm:p-4 rounded-3xl flex flex-col w-[85%] sm:w-[48%] lg:w-[31.5%] shrink-0 snap-center sm:snap-start shadow-md hover:-translate-y-1 hover:shadow-xl transition-all duration-300 cursor-pointer isolate h-72 sm:h-80";

          const handleOpenCard = (): void => {
            if (!canOpenCard(card.plan_ids)) {
              onRestrictedVideoClick();
              return;
            }

            if (card.link) {
              window.open(card.link, '_blank');
            }
          };

          const handleDownloadClick = (event: MouseEvent<HTMLButtonElement>): void => {
            event.stopPropagation();

            if (!canOpenCard(card.plan_ids)) {
              onRestrictedVideoClick();
              return;
            }

            if (card.link) {
              window.open(card.link, '_blank');
            }
          };

          const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleOpenCard();
            }
          };

          return (
            <div
              key={cardIndex}
              role="button"
              tabIndex={0}
              onClick={handleOpenCard}
              onKeyDown={handleCardKeyDown}
              data-course-card
              className={cardClassName}
            >
              <div className="flex justify-between gap-2 mb-3">
                {/* Top Text */}
                <div className="text-white w-auto font-bold bg-[rgb(17,58,40)] text-base sm:text-sm tracking-wide rounded-lg">
                  {card.title}
                </div>
                <button
                  type="button"
                  onClick={handleDownloadClick}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition-colors hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#4CE470]"
                  aria-label={`Download ${card.title}`}
                >
                  <Download size={22} strokeWidth={2} />
                </button>
              </div>

              {/* Inner White Container */}
              <div className="border-[rgb(17_58_40)] border bg-white rounded-2xl flex-1 flex flex-col overflow-hidden group border">
                {/* Main Title at the TOP of the white box */}
                {/* Image restored at the BOTTOM of the white box */}
                <div className="w-full relative flex-1 bg-gray-100 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    width="640"
                    height="360"
                    className="border-[rgb(17 58 40)] object-fill w-full h-full absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-[#113A28]/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                    <PlayCircle size={48} className="text-white drop-shadow-lg scale-90 transition-transform duration-300 group-hover:scale-100" strokeWidth={1.5} />
                  </div>
                </div>

              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// =========================================
// MAIN LIBRARY PAGE
// =========================================
type LibraryMainTab = 'library' | 'live-classes';

const Library = () => {
  const [libraryRows, setLibraryRows] = useState<CourseRowData[]>(courseRows);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [libraryError, setLibraryError] = useState('');
  const [userPlanId, setUserPlanId] = useState<number | null>(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [mainTab, setMainTab] = useState<LibraryMainTab>('library');

  const categories = useMemo(() => Array.from(new Set(libraryRows.map((row) => row.title))), [libraryRows]);
  const [activeCategory, setActiveCategory] = useState(categories[0] ?? '');

  const getCategoryId = (name: string): string => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

  useEffect(() => {
    let isMounted = true;

    const loadLibraryPageData = async () => {
      const token = getAuthToken();

      try {
        setLibraryError('');
        const libraryResponse = await getLibraryRows();
        const rows = libraryResponse.data || [];

        if (isMounted && rows.length > 0) {
          setLibraryRows(rows);
        }
      } catch (error) {
        if (isMounted) {
          setLibraryError(error instanceof Error ? error.message : 'Unable to load library from server.');
        }
      }

      if (!token) {
        return;
      }

      try {
        const dashboardResponse = await getUserDashboard(token);

        if (isMounted) {
          setUserPlanId(dashboardResponse.data.plan_id ?? null);
        }
      } catch {
        if (isMounted) {
          setUserPlanId(null);
        }
      }
    };

    loadLibraryPageData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!categories.length) {
      setActiveCategory('');
      return;
    }

    if (!categories.includes(activeCategory)) {
      setActiveCategory(categories[0]);
    }
  }, [activeCategory, categories]);

  const scrollToCategory = (categoryName: string): void => {
    setActiveCategory(categoryName);
    setIsSidebarOpen(false);
    
    const elementId = getCategoryId(categoryName);
    const element = document.getElementById(elementId);
    
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen flex flex-col bg-[#F4F5F7] font-sans">
        <div className="relative flex flex-1">
          
          {/* MOBILE OVERLAY BACKDROP */}
          {isSidebarOpen && (
            <div 
              className="fixed inset-0 bg-[#113A28]/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}

          {isUpgradeModalOpen && (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#113A28]/50 px-4 backdrop-blur-sm">
              <div className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl">
                <button
                  type="button"
                  onClick={() => setIsUpgradeModalOpen(false)}
                  className="ml-auto flex h-9 w-9 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-[#113A28]"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[#113A28]/10 text-[#113A28]">
                  <PlayCircle size={34} strokeWidth={1.8} />
                </div>
                <h2 className="text-2xl font-extrabold tracking-tight text-[#113A28]">
                  Upgrade Required
                </h2>
                <p className="mt-3 text-sm leading-6 text-gray-600">
                  This video is not in your current plan. Please upgrade your plan to watch this video.
                </p>
                <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                  {/* <button
                    type="button"
                    onClick={() => setIsUpgradeModalOpen(false)}
                    className="flex-1 rounded-xl border border-gray-200 px-5 py-3 text-sm font-bold text-gray-600 transition-colors hover:bg-gray-50"
                  >
                    Not now
                  </button> */}
                  {/* <a
                    href="/pricing"
                    className="flex-1 rounded-xl bg-[#113A28] px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1a573c]"
                  >
                    Upgrade Plan
                  </a> */}
                </div>
              </div>
            </div>
          )}

          {/* SIDEBAR */}
          <aside className={`
            fixed inset-y-0 left-0 z-50 w-[280px] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out
            lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto lg:translate-x-0 lg:shadow-[4px_0_24px_rgba(0,0,0,0.02)] lg:border-r lg:border-gray-200 lg:flex-shrink-0 lg:block lg:z-20
            [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            
            <div className="p-6">
              <div className="flex items-center justify-between px-2 mb-6 lg:px-4">
                <h3 className="text-xs font-bold tracking-widest text-gray-400 uppercase">Categories</h3>
                <button 
                  className="lg:hidden text-gray-400 hover:text-[#4CE470] transition-colors"
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <ul className="pb-10 space-y-2">
                {categories.map((cat, idx) => {
                  const isActive = activeCategory === cat;
                  return (
                    <li key={idx}>
                      <button 
                        onClick={() => scrollToCategory(cat)}
                        className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-300 group flex items-center justify-between ${
                          isActive 
                            ? 'bg-[#113A28] text-white shadow-md' 
                            : 'text-gray-600 hover:bg-gray-50 hover:text-[#113A28]'
                        }`}
                      >
                        {cat}
                        <ChevronRight 
                          size={16} 
                          className={`transition-all ${
                            isActive 
                              ? 'text-[#4CE470] opacity-100 translate-x-0' 
                              : 'opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 text-[#4CE470]'
                          }`} 
                        />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          {/* MAIN CONTENT AREA */}
          <main className="flex-1 bg-[#F4F5F7] p-4 md:p-12 pb-32 overflow-hidden">
            <div className="mx-auto space-y-12 max-w-7xl md:space-y-16">
              
              {/* Header Greeting & Mobile Toggle */}
              <div>
                <button 
                  onClick={() => setIsSidebarOpen(true)}
                  className="lg:hidden flex items-center gap-2 bg-white border border-gray-200 text-[#113A28] px-4 py-2.5 rounded-xl font-bold shadow-sm hover:border-[#4CE470] hover:text-[#4CE470] transition-all mb-6 w-full justify-center sm:w-auto sm:justify-start"
                >
                  <Menu size={18} /> Browse Categories
                </button>

                <h1 className="text-2xl md:text-4xl font-extrabold text-[#113A28] tracking-tight">
                  Welcome back, User.
                </h1>
                <p className="mt-2 text-sm text-gray-500 md:text-base">Pick up where you left off or discover something new.</p>

                <div className="mt-6 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setMainTab('library')}
                    className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                      mainTab === 'library'
                        ? 'bg-[#113A28] text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4CE470]'
                    }`}
                  >
                    Library
                  </button>
                  <button
                    type="button"
                    onClick={() => setMainTab('live-classes')}
                    className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                      mainTab === 'live-classes'
                        ? 'bg-[#113A28] text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4CE470]'
                    }`}
                  >
                    Live Classes
                  </button>
                </div>

                {libraryError && mainTab === 'library' && (
                  <p className="mt-3 text-sm font-medium text-amber-600">
                    Showing saved library data. Server library could not be loaded.
                  </p>
                )}
              </div>

              {mainTab === 'live-classes' ? (
                <LearnerLiveClassesPanel />
              ) : (
                libraryRows.map((row, rowIndex) => (
                  <CourseSlider 
                    key={rowIndex} 
                    row={row} 
                    sectionId={getCategoryId(row.title)} 
                    userPlanId={userPlanId}
                    onRestrictedVideoClick={() => setIsUpgradeModalOpen(true)}
                  />
                ))
              )}

            </div>
          </main>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Library;
